/**
 * Pure logic for the "download the native app" nudges shown to users
 * browsing CareOS on a mobile browser.
 *
 * Never shown inside the native app (Capacitor) nor on desktop.
 * Store URLs are configurable in system_settings, so nothing shows up
 * for a platform whose store link is not published yet.
 */

export type DownloadPlatform = 'android' | 'ios';

export const DOWNLOAD_PROMPT_STORAGE_KEY = 'app_download_prompt_state';

/** Snooze after "Agora não". */
export const DISMISS_SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;
/** Snooze after clicking "Baixar app" — the install may never complete, or the app may be removed. */
export const CONVERTED_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;
/** Snooze for the footer banner close button. */
export const BANNER_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

export interface DownloadPromptState {
  /** Last time the modal was dismissed with "Agora não". */
  dismissedAt?: number;
  /** Last time the user clicked through to the store. */
  convertedAt?: number;
  /** Last time the footer banner was closed. */
  bannerClosedAt?: number;
}

export function parsePromptState(raw: string | null): DownloadPromptState {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const pick = (k: string) => (typeof parsed?.[k] === 'number' ? (parsed[k] as number) : undefined);
    return {
      dismissedAt: pick('dismissedAt'),
      convertedAt: pick('convertedAt'),
      bannerClosedAt: pick('bannerClosedAt'),
    };
  } catch {
    return {};
  }
}

export function readPromptState(): DownloadPromptState {
  try {
    return parsePromptState(localStorage.getItem(DOWNLOAD_PROMPT_STORAGE_KEY));
  } catch {
    return {};
  }
}

export function writePromptState(state: DownloadPromptState): void {
  try {
    localStorage.setItem(DOWNLOAD_PROMPT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable — nudges simply reappear next session */
  }
}

/** Detects the mobile OS from a user agent string. Returns null for desktop/unknown. */
export function detectMobilePlatform(userAgent: string): DownloadPlatform | null {
  const ua = String(userAgent || '').toLowerCase();
  if (/android/.test(ua)) return 'android';
  if (/iphone|ipod/.test(ua) || (/ipad/.test(ua) && !/macintosh/.test(ua))) return 'ios';
  return null;
}

const normalizeUrl = (url: string | null | undefined): string | null => {
  const trimmed = String(url ?? '').trim();
  if (!trimmed || trimmed === 'null') return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : null;
};

export interface EligibilityInput {
  isNativeApp: boolean;
  isMobileViewport: boolean;
  userAgent: string;
  androidUrl?: string | null;
  iosUrl?: string | null;
}

export interface Eligibility {
  eligible: boolean;
  platform: DownloadPlatform | null;
  storeUrl: string | null;
}

/** Whether any download nudge may be shown at all (platform + configured store URL). */
export function resolveEligibility(input: EligibilityInput): Eligibility {
  const none: Eligibility = { eligible: false, platform: null, storeUrl: null };
  if (input.isNativeApp || !input.isMobileViewport) return none;

  const platform = detectMobilePlatform(input.userAgent);
  if (!platform) return none;

  const storeUrl = normalizeUrl(platform === 'android' ? input.androidUrl : input.iosUrl);
  if (!storeUrl) return { ...none, platform };

  return { eligible: true, platform, storeUrl };
}

/** Android app package id — used to open the installed app instead of the store listing. */
export const ANDROID_PACKAGE_ID = 'com.careos.app';

/**
 * Target URL used by the download nudges.
 * On Android an `intent://` URL opens the installed app directly and falls back
 * to the Play Store listing when the app is not installed.
 */
export function buildOpenTarget(platform: DownloadPlatform | null, storeUrl: string, appUrl?: string): string {
  if (platform !== 'android') return storeUrl;
  const fallback = encodeURIComponent(storeUrl);
  const target = appUrl || 'https://careos.life/app';
  const withoutScheme = target.replace(/^https?:\/\//i, '');
  return `intent://${withoutScheme}#Intent;scheme=https;package=${ANDROID_PACKAGE_ID};S.browser_fallback_url=${fallback};end`;
}

/** Whether the first-session modal should open now. */
export function shouldShowDialog(state: DownloadPromptState, now: number): boolean {
  if (state.dismissedAt && now - state.dismissedAt < DISMISS_SNOOZE_MS) return false;
  if (state.convertedAt && now - state.convertedAt < CONVERTED_SNOOZE_MS) return false;
  return true;
}

/** Whether the footer banner should be rendered now. */
export function shouldShowBanner(state: DownloadPromptState, now: number): boolean {
  if (state.bannerClosedAt && now - state.bannerClosedAt < BANNER_SNOOZE_MS) return false;
  if (state.convertedAt && now - state.convertedAt < CONVERTED_SNOOZE_MS) return false;
  return true;
}
